import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CAROUSEL_GAP_PX } from './constants';
import { CarouselArrowNext, CarouselArrowPrev } from './icons';
import ReviewPanel from './ReviewPanel';
import {
  Carousel,
  CarouselButton,
  CarouselCardLink,
  CarouselDot,
  CarouselDots,
  CarouselLiveRegion,
  CarouselNav,
  CarouselSlide,
  CarouselTrack,
  CarouselViewport,
} from './styles';
import { ReviewContent } from './types';

function useDocumentRtl() {
  const [rtl, setRtl] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const read = () => setRtl(document.documentElement.getAttribute('dir') === 'rtl');

    read();
    const observer = new MutationObserver(read);

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });

    return () => observer.disconnect();
  }, []);

  return rtl;
}

type Props = {
  items: ReviewContent[];
};

const TestimonialsCarousel: React.FC<Props> = ({ items }) => {
  const count = items.length;
  const [index, setIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const rtl = useDocumentRtl();
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mq = window.matchMedia('(min-width: 768px)');

    if (!mq) {
      return undefined;
    }

    const apply = () => setSlidesPerView(mq.matches ? 3 : 1);

    apply();

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);

      return () => mq.removeEventListener('change', apply);
    }

    return undefined;
  }, []);

  useEffect(() => {
    const el = viewportRef.current;

    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver(() => {
      setViewportWidth(el.clientWidth);
    });

    ro.observe(el);
    setViewportWidth(el.clientWidth);

    return () => ro.disconnect();
  }, []);

  const maxIndex = Math.max(0, count - slidesPerView);

  useEffect(() => {
    setIndex((i) => Math.min(maxIndex, Math.max(0, i)));
  }, [maxIndex]);

  const slideW =
    viewportWidth > 0
      ? Math.max(
          1,
          Math.floor(
            (viewportWidth - (slidesPerView - 1) * CAROUSEL_GAP_PX) / Math.max(slidesPerView, 1)
          )
        )
      : 0;

  const stride = slideW > 0 ? slideW + CAROUSEL_GAP_PX : 0;
  const translatePx = rtl ? index * stride : -index * stride;

  const go = useCallback(
    (delta: number) => {
      setIndex((i) => Math.min(maxIndex, Math.max(0, i + delta)));
    },
    [maxIndex]
  );

  const goNext = useCallback(() => go(rtl ? -1 : 1), [go, rtl]);
  const goPrev = useCallback(() => go(rtl ? 1 : -1), [go, rtl]);

  const handleCarouselKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  const visibleEnd = Math.min(index + slidesPerView, count);
  const liveLabel =
    slidesPerView >= count
      ? `Testimonials: ${items.map((r) => r.displayName).join(', ')}`
      : `Showing ${index + 1}–${visibleEnd} of ${count}: ${items
          .slice(index, visibleEnd)
          .map((r) => r.displayName)
          .join(', ')}`;

  const dotCount = maxIndex + 1;

  return (
    <Carousel aria-roledescription="carousel" onKeyDown={handleCarouselKeyDown}>
      <CarouselLiveRegion aria-live="polite" aria-atomic="true">
        {liveLabel}
      </CarouselLiveRegion>
      <CarouselViewport ref={viewportRef}>
        <CarouselTrack
          style={
            slideW > 0 && stride > 0
              ? { transform: `translate3d(${translatePx}px, 0, 0)` }
              : undefined
          }
          onTouchStart={(e) => {
            touchStartX.current = e.changedTouches[0]?.clientX ?? null;
          }}
          onTouchEnd={(e) => {
            const start = touchStartX.current;

            touchStartX.current = null;
            if (start == null) return;
            const end = e.changedTouches[0]?.clientX ?? start;
            const dx = end - start;

            if (Math.abs(dx) < 40) return;
            if (rtl) {
              if (dx > 0) goNext();
              else goPrev();
            } else if (dx < 0) goNext();
            else goPrev();
          }}
        >
          {items.map((review) => (
            <CarouselSlide
              key={review.href}
              style={
                slideW > 0
                  ? { flex: '0 0 auto', minWidth: slideW, width: slideW }
                  : { flex: '0 0 100%', minWidth: 0 }
              }
            >
              <CarouselCardLink
                href={review.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Read full Trustpilot review: ${review.title}`}
              >
                <ReviewPanel review={review} />
              </CarouselCardLink>
            </CarouselSlide>
          ))}
        </CarouselTrack>
      </CarouselViewport>

      <CarouselNav>
        <CarouselButton type="button" onClick={goPrev} aria-label="Previous testimonial">
          <CarouselArrowPrev />
        </CarouselButton>
        <CarouselDots role="tablist" aria-label="Testimonials">
          {Array.from({ length: dotCount }, (_, i) => (
            <CarouselDot
              key={`carousel-dot-${i}`}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Show testimonials starting at position ${i + 1} of ${dotCount}`}
              $active={i === index}
              onClick={() => setIndex(i)}
            />
          ))}
        </CarouselDots>
        <CarouselButton type="button" onClick={goNext} aria-label="Next testimonial">
          <CarouselArrowNext />
        </CarouselButton>
      </CarouselNav>
    </Carousel>
  );
};

export default TestimonialsCarousel;
